import { useAppSelector } from '@frontend/common';
import { DateInput, DurationInput } from '@frontend/components/basic-forms';
import { AsyncComponent } from '@frontend/components/common';
import { EnumSelectListV2, MultiObjectSelectMapper, ObjectSelectMapper } from '@frontend/components/select';
import { useActiveModal, useModals } from '@frontend/modal';
import { ListInfo, addActiveFilter, listStore, removeActiveFilter } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { LuArrowLeft } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

const PLURAL_KEYS = ['senders', 'receivers', 'owners', 'users', 'products'];
const TIME_KEYS = ['before', 'after', 'update_timestamp', 'creation_timestamp'];

export interface FilterModalProps {
    filterKeys: string[];
    extraProps?: Map<string, object>;
    dispatch: ThunkDispatch<any, any, Action>;
    listId: string;
    displayName?: Map<string, React.ReactNode>;
    queryKeyMap?: Map<string, string>;
    enumOverwrite?: Map<string, keyof typeof EnumSelectListV2>;
}

export const FilterModal = (props: FilterModalProps) => {
    const list = useAppSelector(useSelector, listStore)[props.listId];
    const { closeModal } = useModals();
    const { modalId } = useActiveModal();
    const Filters = useMemo(() => convert(props, list), [props, list]);

    return (
        <div className='bg-background-surface border-border-default flex h-full w-full flex-col gap-8 border-l p-4'>
            <div className='inline-flex w-full items-end justify-between'>
                <div className='flex items-center justify-start gap-3'>
                    <div
                        onClick={() => closeModal(modalId)}
                        className='bg-background-surface border-border-default hover:bg-brand-light relative flex size-11 cursor-pointer items-center justify-center self-center rounded-lg border'>
                        <LuArrowLeft className='text-brand-muted size-6' />
                    </div>
                    <div className='text-foreground-default justify-start text-3xl font-semibold'>
                        <FormattedMessage
                            id='components.insane-filter.filter-table'
                            defaultMessage='Filter table'
                        />
                    </div>
                </div>
            </div>
            <div className='flex flex-col gap-4'>{Filters}</div>
        </div>
    );
};

function convert(props: FilterModalProps, list: ListInfo) {
    return props.filterKeys.map((key) => {
        if (typeof key !== 'string') return;
        const extraProps = props.extraProps ? props.extraProps.get(key) : {};
        const displayName = props.displayName ? props.displayName.get(key) : undefined;
        const queryKeyOverride = props.queryKeyMap ? props.queryKeyMap.get(key) : undefined;
        if (key.endsWith('_id')) {
            return (
                <ObjectSelectMapper
                    {...extraProps}
                    label={displayName}
                    id={key}
                    objectKey={key}
                    queryKeyOverride={queryKeyOverride}
                    submitted={false}
                    isClearable
                    allowURLChange
                    onChange={(v) => {
                        if (v) {
                            if (!Array.isArray(v)) {
                                props.dispatch(addActiveFilter({ listId: props.listId, key: key, value: v }));
                            }
                        } else {
                            props.dispatch(removeActiveFilter({ listId: props.listId, key: key }));
                        }
                    }}
                />
            );
        } else if (PLURAL_KEYS.includes(key)) {
            return (
                <MultiObjectSelectMapper
                    {...extraProps}
                    queryKeyOverride={queryKeyOverride}
                    label={displayName}
                    key={key}
                    id={key}
                    objectKey={key}
                    submitted={false}
                    useConditionedStyling={false}
                    onChange={(newValue) => {
                        if (newValue.length == list.activeFilters?.[key]?.length) return;
                        if (newValue.length === 0 || newValue === null) {
                            props.dispatch(removeActiveFilter({ listId: props.listId, key: key }));
                        } else if (newValue.length < (list.activeFilters?.[key]?.length ?? 0)) {
                            const oldValue = list.activeFilters?.[key] ?? [];
                            const removed = oldValue.filter((v: string) => !newValue.includes(v));
                            removed.forEach((value) => {
                                if (!value) return;
                                props.dispatch(removeActiveFilter({ listId: props.listId, key: key, value }));
                            });
                        } else {
                            props.dispatch(addActiveFilter({ listId: props.listId, key: key, value: newValue }));
                        }
                    }}
                    isClearable
                    allowURLChange
                />
            );
        } else if (key === 'waiting') {
            return (
                <DurationInput
                    label={Waiting}
                    onApplyFilter={(duration, unit, period, payload) => {
                        if (payload.value === undefined) {
                            props.dispatch(removeActiveFilter({ listId: props.listId, key: payload.key }));
                            return;
                        }

                        props.dispatch(addActiveFilter({ listId: props.listId, key: payload.key, value: payload.value }));
                    }}
                    allowURLChange
                />
            );
        } else if (TIME_KEYS.includes(key)) {
            return (
                <DateInput
                    submitted={false}
                    label={displayName || LABELS[key] || key}
                    id={key}
                    key={key}
                    value={list.activeFilters?.[key] ? new Date(list.activeFilters[key][0]) : undefined}
                    onChange={(date) => {
                        if (!date) {
                            props.dispatch(removeActiveFilter({ listId: props.listId, key: key }));
                        } else {
                            props.dispatch(addActiveFilter({ listId: props.listId, key: key, value: format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") }));
                        }
                    }}
                    allowURLChange
                    queryParamKey={key}
                />
            );
        } else if (
            (props.enumOverwrite && props.enumOverwrite.has(key) && EnumSelectListV2[props.enumOverwrite.get(key) as keyof typeof EnumSelectListV2]) ||
            EnumSelectListV2[key as keyof typeof EnumSelectListV2]
        ) {
            const EnumInput =
                props.enumOverwrite && props.enumOverwrite.has(key)
                    ? EnumSelectListV2[props.enumOverwrite.get(key) as keyof typeof EnumSelectListV2]
                    : EnumSelectListV2[key as keyof typeof EnumSelectListV2];
            return (
                <AsyncComponent>
                    <EnumInput
                        label={displayName}
                        id={key}
                        key={key}
                        value={list.activeFilters?.[queryKeyOverride || key] ? list.activeFilters[queryKeyOverride || key][0] : undefined}
                        isClearable
                        onSelect={(v) => {
                            if (v) {
                                props.dispatch(addActiveFilter({ listId: props.listId, key: queryKeyOverride || key, value: v.value }));
                            } else {
                                props.dispatch(removeActiveFilter({ listId: props.listId, key: queryKeyOverride || key }));
                            }
                        }}
                        useQueryParam={queryKeyOverride || key}
                    />
                </AsyncComponent>
            );
        } else {
            return <div>{key}</div>;
        }
    });
}

const Before = (
    <FormattedMessage
        id='components.insane-filter.before'
        defaultMessage='Before'
    />
);
const After = (
    <FormattedMessage
        id='components.insane-filter.after'
        defaultMessage='After'
    />
);
const Waiting = (
    <FormattedMessage
        id='components.insane-filter.waiting'
        defaultMessage='Waiting'
    />
);
const LABELS: Record<string, React.ReactNode> = {
    before: Before,
    after: After,
    waiting: Waiting
};
